import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import { useEnabledModuleTabPaths } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/useEnabledModuleTabPaths';
import { usePrepModuleTabProps } from '~/scenes/PortalViews/Views/EmployeeViews/Modules/components/EmployeeModuleTabs/usePrepModuleTabProps';
import {
  EmployeeModuleRouting_Development,
  EmployeeModuleTabPath_Development,
} from '~/scenes/PortalViews/Views/EmployeeViews/Modules/DevelopmentModule/data';
import {
  EmployeeModuleTabAccessMode,
  EmployeeModuleTabProps,
  EmployeeModuleTabSetupProps,
} from '~/scenes/PortalViews/Views/EmployeeViews/Modules/types';

type PropsType = {
  hasFullAccess: boolean;
  modulePath: string;
};

type UseEmployeeDevelopmentModuleOutput<TabPath> = {
  isLoading: boolean;
  employeeId: number;
  enabledTabPaths: TabPath[];
  defaultTab: TabPath;
  modulePath: string;
  tabs: EmployeeModuleTabProps<TabPath>[];
};

export const useEmployeeDevelopmentModule = (
  props: PropsType,
): UseEmployeeDevelopmentModuleOutput<EmployeeModuleTabPath_Development> => {
  const { hasFullAccess, modulePath } = props;

  const { t } = useTranslation();
  const { selectedEmployeeId } = useSelectedEmployeeId();

  const { availableTabs } = useMemo(() => {
    const allTabs = Object.values(EmployeeModuleTabPath_Development);

    // NOTE: No conditions yet, but likely added later
    const tabConditions: {
      [key in EmployeeModuleTabPath_Development]?: boolean;
    } = {
      // [EmployeeModuleTabPath_People.DecisionRegistry]:
      // hasData &&
      // applicationConfig?.fComConfig?.miscellaneousConfig
      //   ?.fComEnableDecisionRegistry,
    };

    const availableTabs: EmployeeModuleTabPath_Development[] = hasFullAccess
      ? allTabs.filter((path) => {
          return Object.keys(tabConditions).includes(path)
            ? tabConditions[path]
            : true;
        })
      : [];

    return { tabConditions, availableTabs };
  }, [hasFullAccess]);

  const tabProps: EmployeeModuleTabSetupProps<EmployeeModuleTabPath_Development>[] =
    [
      {
        id: EmployeeModuleTabPath_Development.Experience,
        title: t('laExperience'),
      },
      {
        id: EmployeeModuleTabPath_Development.Education,
        title: t('laEducation'),
      },
      {
        id: EmployeeModuleTabPath_Development.Competencies,
        title: t('laCompetencies'),
      },
      {
        id: EmployeeModuleTabPath_Development.DevelopmentPlan,
        title: t('laDevPlan'),
      },
      {
        id: EmployeeModuleTabPath_Development.JobReqEvaluation,
        title: t('laJobReqEval'),
      },
    ].map((p) => ({ ...p, accessMode: EmployeeModuleTabAccessMode.Full }));

  const { enabledTabConfigs, enabledTabPaths, defaultTab } =
    useEnabledModuleTabPaths({
      ...EmployeeModuleRouting_Development,
      availableTabs,
    });

  const { tabs, isLoading: isLoadingTabPrep } = usePrepModuleTabProps({
    ...EmployeeModuleRouting_Development,
    tabConfigs: enabledTabConfigs,
    modulePath,
    tabsSetupProps: tabProps,
    enabledTabPaths,
  });

  return {
    isLoading: isLoadingTabPrep,
    enabledTabPaths,
    defaultTab,
    modulePath,
    employeeId: selectedEmployeeId,
    tabs,
  };
};
